import { Subscription } from '../types/subscription';
import { SUBSCRIPTION_PLAN_IDS } from '../types/subscription';
import { TrialExtensionUtils, TRIAL_EXTENSION_CONSTANTS } from '../constants/trialExtension';
import { subscriptionService } from './subscriptionService';

/**
 * Service to handle trial extension logic for existing and new users
 */
export class TrialExtensionService {
  /**
   * Create or update subscription for existing users with extended trial
   * @param userId - User ID
   * @param userRegistrationDate - When the user originally registered
   * @param userName - User's full name
   * @param userPhone - User's phone number
   * @returns Created or updated subscription
   */
  async createOrUpdateExtendedTrialSubscription(
    userId: string, 
    userRegistrationDate?: Date,
    userName?: string,
    userPhone?: string
  ): Promise<Subscription> {
    try {
      console.log('🔄 TrialExtensionService: Creating/updating extended trial subscription for user:', userId);
      
      const now = Date.now();
      const registrationDate = userRegistrationDate || new Date();
      
      // Determine trial end date based on registration date
      const trialEndDate = TrialExtensionUtils.getTrialEndDate(registrationDate);
      const isExtendedTrial = TrialExtensionUtils.shouldGetExtendedTrial(registrationDate);
      
      console.log('🔄 TrialExtensionService: Trial details:', {
        registrationDate: registrationDate.toISOString(),
        trialEndDate: trialEndDate.toISOString(),
        isExtendedTrial,
        daysRemaining: isExtendedTrial 
          ? TrialExtensionUtils.getExtendedTrialDaysRemaining()
          : TrialExtensionUtils.getStandardTrialDaysRemaining(registrationDate)
      });
      
      // Create subscription data
      const subscriptionData = {
        userId,
        userName: userName || '',
        userPhone: userPhone || '',
        planId: TRIAL_EXTENSION_CONSTANTS.EXTENDED_TRIAL_PLAN_ID,
        status: TRIAL_EXTENSION_CONSTANTS.TRIAL_STATUS,
        startDate: now.toString(),
        endDate: trialEndDate.getTime().toString(),
        totalPrice: 0, // Trial is free
        currency: TRIAL_EXTENSION_CONSTANTS.CURRENCY,
        billingCycle: 'monthly' as const,
        isActive: true,
        // Extended trial metadata
        trialExtensionReason: isExtendedTrial ? 'business_extension' as const : 'standard_trial' as const,
        createdBy: userId,
        updatedBy: userId
      };
      
      // Create or update subscription
      console.log('🔄 TrialExtensionService: About to create subscription with data:', subscriptionData);
      const subscription = await subscriptionService.createSubscription(subscriptionData);
      
      console.log('✅ TrialExtensionService: Extended trial subscription created successfully:', subscription);
      
      // Verify the subscription was created by trying to fetch it
      const verifySubscription = await subscriptionService.getUserSubscription(userId);
      console.log('🔍 TrialExtensionService: Verification - fetched subscription:', verifySubscription);
      
      return subscription;
      
    } catch (error) {
      console.error('❌ TrialExtensionService: Error creating extended trial subscription:', error);
      throw new Error('Failed to create extended trial subscription');
    }
  }
  
  /**
   * Create subscription for new users based on registration date
   * @param userId - User ID
   * @param registrationDate - When the user registered
   * @returns Created subscription
   */
  async createNewUserTrialSubscription(
    userId: string, 
    registrationDate: Date = new Date()
  ): Promise<Subscription> {
    try {
      console.log('🔄 TrialExtensionService: Creating new user trial subscription:', {
        userId,
        registrationDate: registrationDate.toISOString()
      });
      
      return await this.createOrUpdateExtendedTrialSubscription(userId, registrationDate);
      
    } catch (error) {
      console.error('❌ TrialExtensionService: Error creating new user trial subscription:', error);
      throw error;
    }
  }
  
  /**
   * Migrate existing user to extended trial
   * @param userId - User ID
   * @param existingSubscription - Current subscription (if any)
   * @param userRegistrationDate - When the user originally registered
   * @returns Updated subscription
   */
  async migrateExistingUserToExtendedTrial(
    userId: string,
    existingSubscription: Subscription | null,
    userRegistrationDate?: Date
  ): Promise<Subscription> {
    try {
      console.log('🔄 TrialExtensionService: Migrating existing user to extended trial:', {
        userId,
        hasExistingSubscription: !!existingSubscription,
        userRegistrationDate: userRegistrationDate?.toISOString()
      });
      
      // If user already has a subscription, check if it needs updating
      if (existingSubscription) {
        const registrationDate = userRegistrationDate || new Date(parseInt(existingSubscription.startDate));
        const shouldGetExtendedTrial = TrialExtensionUtils.shouldGetExtendedTrial(registrationDate);
        
        // If user should get extended trial and current trial ends before 31/12/2025, update it
        if (shouldGetExtendedTrial && existingSubscription.endDate) {
          const currentTrialEnd = new Date(parseInt(existingSubscription.endDate));
          const extendedTrialEnd = TrialExtensionUtils.getExtendedTrialEndDate();
          
          if (currentTrialEnd < extendedTrialEnd) {
            console.log('🔄 TrialExtensionService: Updating existing trial to extended trial');
            
            const updatedSubscription = {
              ...existingSubscription,
              planId: TRIAL_EXTENSION_CONSTANTS.EXTENDED_TRIAL_PLAN_ID,
              endDate: extendedTrialEnd.getTime().toString(),
              trialExtensionReason: 'business_extension' as const,
              updatedAt: Date.now().toString(),
              updatedBy: userId
            };
            
            await subscriptionService.updateSubscription(userId, updatedSubscription);
            return updatedSubscription as Subscription;
          }
        }
        
        // If no update needed, return existing subscription
        return existingSubscription;
      }
      
      // No existing subscription, create new extended trial
      return await this.createOrUpdateExtendedTrialSubscription(userId, userRegistrationDate);
      
    } catch (error) {
      console.error('❌ TrialExtensionService: Error migrating existing user:', error);
      throw error;
    }
  }
  
  /**
   * Check if user is eligible for extended trial
   * @param registrationDate - When the user registered
   * @returns True if eligible for extended trial
   */
  isEligibleForExtendedTrial(registrationDate: Date): boolean {
    return TrialExtensionUtils.shouldGetExtendedTrial(registrationDate);
  }
  
  /**
   * Get trial information for a user
   * @param subscription - User's subscription
   * @returns Trial information
   */
  getTrialInfo(subscription: Subscription): {
    isInTrial: boolean;
    isExtendedTrial: boolean;
    endDate: Date;
    daysRemaining: number;
    trialType: 'extended' | 'standard' | 'none';
  } {
    const isInTrial = subscription.status === 'trial';
    const endDate = subscription.endDate 
      ? new Date(parseInt(subscription.endDate))
      : new Date();
    
    // Check if this is an extended trial based on trialExtensionReason
    const isExtendedTrial = subscription.trialExtensionReason === 'business_extension' || 
      endDate.getTime() === TRIAL_EXTENSION_CONSTANTS.EXTENDED_TRIAL_END_DATE.getTime();
    
    const daysRemaining = isInTrial 
      ? (isExtendedTrial 
          ? TrialExtensionUtils.getExtendedTrialDaysRemaining()
          : TrialExtensionUtils.getStandardTrialDaysRemaining(new Date(parseInt(subscription.startDate)))
        )
      : 0;
    
    return {
      isInTrial,
      isExtendedTrial,
      endDate,
      daysRemaining,
      trialType: isInTrial ? (isExtendedTrial ? 'extended' : 'standard') : 'none'
    };
  }
  
  /**
   * Check if current time is within any active trial period
   * @returns True if within extended trial period
   */
  isWithinAnyTrialPeriod(): boolean {
    return TrialExtensionUtils.isWithinExtendedTrialPeriod();
  }
}

// Export singleton instance
export const trialExtensionService = new TrialExtensionService();
